'use client'

import { Loader2 } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { UserEntryRevisionStatusType } from './entryTypes'

// Same Reject / Accept / Accept Hidden / Defer action row CasosView's "Case
// Actions" panel has, as a standalone widget. Purely presentational — the
// caller supplies the current revision status (to grey out whichever action
// is already applied) and the four handlers; this doesn't call the backend
// itself.
export function AcceptEntryWidget({
  theme,
  currentRevisionStatus,
  loadingAction,
  onAccept,
  onAcceptHidden,
  onReject,
  onDefer,
  isScam,
  onIsScamChange,
}: {
  theme: ProcessedTheme
  currentRevisionStatus?: string
  loadingAction?: UserEntryRevisionStatusType
  onAccept: () => void
  onAcceptHidden: () => void
  onReject: () => void
  onDefer: () => void
  /** Same underlying value ScamClassificationForm's "É burla?" switch edits — surfaced here too since it's what Accept/Accept Hidden actually publish, so it shouldn't require scrolling to the Classification panel to check or flip. */
  isScam?: boolean | null
  onIsScamChange: (value: boolean) => void
}) {
  const c = watchClasses(theme)
  // Accept/Accept Hidden stay enabled even once the entry already carries
  // that same status — re-clicking is how an edit made after approval (a
  // corrected platform, a fixed typo, a reclassification) actually gets
  // saved, since the accept payload always carries whatever's currently in
  // the form. Reject/Defer carry no edited payload, so re-applying an
  // already-applied one is a no-op worth graying out.
  const disabled = (status: UserEntryRevisionStatusType) =>
    Boolean(loadingAction) || ((status === 'reject' || status === 'defer1') && currentRevisionStatus === status)

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={onReject}
        disabled={disabled('reject')}
        className="rounded border border-destructive px-3 py-1.5 text-[12px] font-semibold text-destructive transition-colors hover:bg-danger-surface disabled:opacity-40"
      >
        {loadingAction === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Reject'}
      </button>
      <button
        onClick={onAccept}
        disabled={disabled('accept')}
        className="rounded bg-success text-success-foreground px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-success disabled:opacity-40"
      >
        {loadingAction === 'accept' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Accept'}
      </button>
      <button
        onClick={onAcceptHidden}
        disabled={disabled('accept-hidden')}
        className={`rounded px-3 py-1.5 text-[12px] font-semibold text-white transition-colors disabled:opacity-40 ${theme === 'dark' ? 'bg-muted hover:bg-muted' : 'bg-subtle hover:bg-muted'}`}
      >
        {loadingAction === 'accept-hidden' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Accept Hidden'}
      </button>
      <button
        onClick={onDefer}
        disabled={disabled('defer1')}
        className={`rounded border px-3 py-1.5 text-[12px] font-semibold ${c.line} ${c.muted} disabled:opacity-40`}
      >
        {loadingAction === 'defer1' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Defer'}
      </button>
      <div className={`ml-1 flex items-center gap-2 border-l pl-3 ${c.line}`}>
        <span className={`text-[12px] font-semibold ${isScam ? 'text-destructive' : c.muted}`}>É burla?</span>
        <Switch checked={isScam ?? false} onCheckedChange={onIsScamChange} />
      </div>
    </div>
  )
}
